import { ResourceInfo, ResourceTypeLiteral } from '@sharefiledev/fileviewer-pilet-sdk';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import { FilesHttpService, WatermarkFileInformation } from '../../api/FilesHttpService';
import { t } from '../../translate';
import { isResourceFileType, isResourcesOfSameType } from '../../utils/ridUtils';
import { isResourceAllowedForWatermark } from './isResourceAllowedForWatermark';

export type WatermarkOperationResult =
	| { success: true; resourceRIDs: string[] }
	| { success: false; errorMessage: string };

export type ValidateResourcesResult =
	| { success: true }
	| { success: false; errorMessage: string };

export type WatermarkReadResult =
	| { success: true; watermarkFileInformation: WatermarkFileInformation }
	| { success: false; errorMessage: string };

export function validateResources(resources: ResourceInfo[]): ValidateResourcesResult {
	if (!resources || resources.length === 0) {
		return {
			success: false,
			errorMessage: t('WatermarkCheckBox.NoResourcesProvidedError'),
		};
	}

	const allValid = resources.every(resource =>
		isResourceAllowedForWatermark(resource.resourceName, resource.resourceSize)
	);

	if (!allValid) {
		return {
			success: false,
			errorMessage: t('WatermarkCheckBox.ResourceNotSupportedError'),
		};
	}

	const segment = RID.parse(resources[0].resourceRID);
	if (
		!segment ||
		!isResourceFileType(segment.entityType) ||
		!isResourcesOfSameType(
			resources.map(r => r.resourceRID),
			segment.entityType
		)
	) {
		return {
			success: false,
			errorMessage: t('WatermarkCheckBox.ResourceNotSupportedError'),
		};
	}

	return { success: true };
}

export async function createWatermarkResource(
	resourceRids: string[]
): Promise<WatermarkOperationResult> {
	const entityType = RID.parse(resourceRids[0])?.entityType;
	const request = { resourceList: resourceRids };
	try {
		const response =
			entityType === ResourceTypeLiteral.FileVersion
				? await FilesHttpService.postWatermarkFileVersionCreateAsync(request)
				: await FilesHttpService.postWatermarkFileCreateAsync(request);

		const resultRIDs = response?.resourceList ?? [];
		if (!resultRIDs.length) {
			return {
				success: false,
				errorMessage: t('WatermarkCheckBox.FailedToCreateWatermarkResource'),
			};
		}

		return { success: true, resourceRIDs: resultRIDs };
	} catch (error) {
		return {
			success: false,
			errorMessage: t('WatermarkCheckBox.FailedToCreateWatermarkResource'),
		};
	}
}

export async function deleteWatermarkResource(
	transformedRIDs: string[]
): Promise<WatermarkOperationResult> {
	if (!transformedRIDs.length) {
		return { success: true, resourceRIDs: [] };
	}

	try {
		const entityType = RID.parse(transformedRIDs[0])?.entityType;
		const isSuccessful: boolean =
			entityType === ResourceTypeLiteral.WatermarkedFile
				? await FilesHttpService.postWatermarkFileDeleteAsync({
						resourceList: transformedRIDs,
				  })
				: await FilesHttpService.postWatermarkFileVersionDeleteAsync({
						resourceList: transformedRIDs,
				  });

		if (!isSuccessful) {
			return {
				success: false,
				errorMessage: t('WatermarkCheckBox.FailedToDeleteWatermarkResource'),
			};
		}
		return { success: true, resourceRIDs: [] };
	} catch (error) {
		return {
			success: false,
			errorMessage: t('WatermarkCheckBox.FailedToDeleteWatermarkResource'),
		};
	}
}

export async function getWatermarkResource(
	resourceRIDs: string[]
): Promise<WatermarkReadResult> {
	if (!resourceRIDs.length) {
		return { success: true, watermarkFileInformation: { resources: [] } };
	}

	const entityType = RID.parse(resourceRIDs[0])?.entityType;

	const request = { resourceList: resourceRIDs };

	try {
		const response =
			entityType === ResourceTypeLiteral.WatermarkedFileVersion
				? await FilesHttpService.getWatermarkedFileVersionAsync(request)
				: await FilesHttpService.getWatermarkedFileAsync(request);

		if (!response) {
			return {
				success: false,
				errorMessage: t('WatermarkCheckBox.FailedToGetWatermarkResource'),
			};
		}
		return { success: true, watermarkFileInformation: response };
	} catch {
		return {
			success: false,
			errorMessage: t('WatermarkCheckBox.FailedToGetWatermarkResource'),
		};
	}
}
